import { forwardRef, useEffect, useState } from "react";
import useStore from "../../Store/Store";
import { logger } from "../Logger";
import { CustomContentProps, SnackbarContent } from "notistack";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import { styled } from "@mui/material/styles";
import { theme } from "../../Themes/Themes";
import { ClickAwayListener, Slider, Typography, Box } from "@mui/material";
import { centerGraph } from "../DiagramUtils";
import {
  useResponsiveValue,
  hideIrrelevantCellsToggleConfig,
} from "../responsive";

const StyledHelpIcon = styled(HelpOutlineSharpIcon, {
  shouldForwardProp: (prop) => prop !== "showLabels",
})<{ showLabels?: boolean }>(({ showLabels }) => ({
  position: "absolute",
  top: showLabels ? "35px" : "6px", // Move down when expanded to stay aligned with bar
  right: "12px",
  color: theme.custom.pink,
  fontSize: "16px",
  cursor: "pointer",
  zIndex: 10,
  transition: "top 0.2s ease-in-out", // Smooth transition
}));

const StyledCard = styled("div", {
  shouldForwardProp: (prop) => prop !== "showLabels",
})<{ showLabels?: boolean }>(({ showLabels }) => ({
  position: "relative",
  borderRadius: "30px",
  color: "#fffffffc",
  border: `1px solid ${theme.custom.lighterPink}`,
  height: showLabels ? "80px" : "30px", // Expand height on hover to show labels
  width: "475px", // 35% wider than 350px
  background: "rgba(255, 255, 255, 0.4)",
  display: "flex",
  alignItems: "center", // Always center vertically
  boxShadow: "none",
  padding: "0 20px",
  transition: "height 0.2s ease-in-out",
  overflow: "visible", // Allow clouds to extend outside
}));

const ContentContainer = styled("div")({
  width: "100%",
  display: "flex",
  alignItems: "center", // Keep everything centered
  gap: "16px",
  position: "relative",
});

const Title = styled(Typography)({
  fontWeight: 500,
  fontSize: "14px",
  color: theme.custom.translucentPink,
  whiteSpace: "nowrap",
  minWidth: "90px",
  // Remove marginTop - let flexbox center it
});

const SliderContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showLabels",
})<{ showLabels?: boolean }>(({ showLabels }) => ({
  flex: 1,
  position: "relative",
  paddingRight: "30px", // Add space for help icon
  paddingTop: "3px", // Shift slider down by 2px
}));

const StyledSlider = styled(Slider)({
  color: "#FFAEA7", // Use the specific color requested
  height: 4,
  padding: "8px 0",
  "& .MuiSlider-track": {
    border: "none",
    backgroundColor: "#FFAEA7", // Use the specific color requested
    height: 4,
  },
  "& .MuiSlider-rail": {
    backgroundColor: theme.custom.lighterPink,
    opacity: 0.3,
    height: 4,
  },
  "& .MuiSlider-thumb": {
    height: 16,
    width: 16,
    backgroundColor: "#fff",
    border: `2px solid #FFAEA7`, // Use the specific color requested
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      boxShadow: `0 0 0 6px rgba(255, 174, 167, 0.12), 0 2px 8px rgba(0,0,0,0.15)`, // #FFAEA7 with opacity
    },
    "&:before": {
      display: "none",
    },
  },
  "& .MuiSlider-mark": {
    display: "none", // Hide the marks (red dashes) completely
  },
  "& .MuiSlider-markLabel": {
    display: "none", // Hide default mark labels - we'll use custom clouds
  },
});

// Cloud-style label containers
const CloudLabel = styled("div", {
  shouldForwardProp: (prop) =>
    !["position", "showLabels"].includes(prop as string),
})<{ position: "left" | "center" | "right"; showLabels: boolean }>(
  ({ position, showLabels }) => ({
    position: "absolute",
    top: "-45px", // Position above the slider
    left: position === "left" ? "-13%" : position === "center" ? "45%" : "101%", // Align with actual slider track positions
    transform:
      position === "center"
        ? "translateX(-50%)"
        : position === "right"
        ? "translateX(-100%)"
        : "none",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    border: `1px solid ${theme.custom.lighterPink}`,
    borderRadius: "12px",
    padding: "6px 10px",
    fontSize: "10px",
    color: theme.custom.translucentPink,
    fontWeight: 400,
    lineHeight: "1.2",
    textAlign: "center",
    maxWidth: "85px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    opacity: showLabels ? 1 : 0,
    visibility: showLabels ? "visible" : "hidden",
    transition: "opacity 0.2s ease-in-out, visibility 0.2s ease-in-out",
    zIndex: 5,
    // Arrow pointing down to the bar with pink border
    "&::after": {
      content: '""',
      position: "absolute",
      top: "100%",
      left: "50%", // Center the arrow on each cloud
      transform: "translateX(-50%)",
      width: 0,
      height: 0,
      borderLeft: "4px solid transparent",
      borderRight: "4px solid transparent",
      borderTop: `4px solid rgba(255, 255, 255, 0.95)`,
    },
    // Add pink border to the arrow tip
    "&::before": {
      content: '""',
      position: "absolute",
      top: "100%",
      left: "50%", // Center the arrow border on each cloud
      transform: "translateX(-50%)",
      width: 0,
      height: 0,
      borderLeft: "5px solid transparent",
      borderRight: "5px solid transparent",
      borderTop: `5px solid ${theme.custom.lighterPink}`,
      zIndex: -1,
    },
  })
);

const ExitLink = styled("span")({
  color: theme.custom.pinkerPink,
  cursor: "pointer",
  fontWeight: 600,
  textDecoration: "underline",
});

const TooltipContent = styled("div")({
  padding: "8px 10px",
  fontSize: "14px",
  lineHeight: "1.4",
});

type DetailLevel = 1 | 2 | 3;

const LevelOfDetailsSlider = forwardRef<HTMLDivElement, CustomContentProps>(
  (props, ref) => {
    const {
      hideIrrelevantCells,
      selectedSimulationKey,
      considerParentsAsRelevantCells,
    } = useStore((state) => ({
      hideIrrelevantCells: state.hideIrrelevantCells,
      selectedSimulationKey: state.simulationsState.selectedSimulationKey,
      considerParentsAsRelevantCells: state.considerParentsAsRelevantCells,
    }));

    // Get responsive positioning values
    const responsiveConfig = useResponsiveValue(
      hideIrrelevantCellsToggleConfig
    );

    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [showLabels, setShowLabels] = useState(false);

    const isInSimulationMode = !!selectedSimulationKey;

    // Determine current level based on existing state
    const getCurrentLevel = (): DetailLevel => {
      if (!hideIrrelevantCells) {
        return 1; // Show Full Diagram
      }
      if (considerParentsAsRelevantCells) {
        return 2; // Show selected + ancestors
      }
      return 3; // Show selected only
    };

    // Get default level when simulation is selected
    const getDefaultLevel = (): DetailLevel => {
      return isInSimulationMode ? 2 : getCurrentLevel();
    };

    const [currentLevel, setCurrentLevel] = useState<DetailLevel>(
      getDefaultLevel()
    );

    // Update level when simulation mode changes
    useEffect(() => {
      if (isInSimulationMode) {
        setCurrentLevel(2); // Default to stage 2 when simulation is selected
        handleLevelChange(2);
      }
    }, [isInSimulationMode]);

    // Get labels based on simulation mode
    const getLabels = () => {
      if (isInSimulationMode) {
        return [
          "Show in Full Diagram",
          "Simsteps + their ancestors",
          "Simsteps only",
        ];
      } else {
        return [
          "Show in Full Diagram",
          "cell connections + ancestors",
          "cell and its connections only",
        ];
      }
    };

    const labels = getLabels();

    const handleLevelChange = async (level: DetailLevel) => {
      setCurrentLevel(level);

      // Map level to existing boolean logic
      switch (level) {
        case 1: // Show Full Diagram
          useStore.getState().setHideIrrelevantCells(false);
          useStore.getState().setConsiderParentsAsRelevantCells(true);
          break;
        case 2: // Show selected + ancestors
          useStore.getState().setHideIrrelevantCells(true);
          useStore.getState().setConsiderParentsAsRelevantCells(true);
          break;
        case 3: // Show selected only
          useStore.getState().setHideIrrelevantCells(true);
          useStore.getState().setConsiderParentsAsRelevantCells(false);
          break;
      }

      // Center the graph after a delay to allow visibility changes to complete
      setTimeout(async () => {
        try {
          await centerGraph();
        } catch (error) {
          logger.error(
            "Error centering graph from Level of Details slider:",
            error
          );
        }
      }, 600);
    };

    const handleExitSimulation = (event: React.MouseEvent) => {
      event.stopPropagation();
      useStore.getState().setSelectedSimulationKey("");
      setTooltipOpen(false);
    };

    const handleTooltipClose = () => {
      setTooltipOpen(false);
    };

    const handleTooltipToggle = () => {
      setTooltipOpen((prev) => !prev);
    };

    const handleMouseEnter = () => {
      setShowLabels(true);
    };

    const handleMouseLeave = () => {
      setShowLabels(false);
    };

    // Calculate responsive top position
    const getTopPosition = () => {
      const isRunningSimulation = useStore.getState().isRunningSimulation;
      return isRunningSimulation
        ? responsiveConfig.runningSimulation
        : responsiveConfig.normalMode;
    };

    return (
      <SnackbarContent
        ref={ref}
        className="SnackBarWithToggle"
        style={{ top: getTopPosition(), left: "-29vw" }}
      >
        <StyledCard
          showLabels={showLabels}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Help Icon in top right corner */}
          {isInSimulationMode && (
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <div>
                <Tooltip
                  title={
                    <TooltipContent>
                      To only show cells connected to the current cell,{" "}
                      <ExitLink onClick={handleExitSimulation}>exit</ExitLink>{" "}
                      simulation mode
                    </TooltipContent>
                  }
                  placement="top"
                  open={tooltipOpen}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                >
                  <StyledHelpIcon
                    showLabels={showLabels}
                    onClick={handleTooltipToggle}
                    data-testid="irrelevant-cells-help-icon"
                  />
                </Tooltip>
              </div>
            </ClickAwayListener>
          )}

          <ContentContainer>
            <Title>Level of Details</Title>

            <SliderContainer showLabels={showLabels}>
              <StyledSlider
                value={currentLevel}
                onChange={(_, value) => handleLevelChange(value as DetailLevel)}
                step={1}
                marks={[]} // No marks needed
                min={1}
                max={3}
                valueLabelDisplay="off"
                data-testid="level-of-details-slider"
              />

              {/* Cloud labels pointing to bar positions */}
              <CloudLabel position="left" showLabels={showLabels}>
                {labels[0]}
              </CloudLabel>
              <CloudLabel position="center" showLabels={showLabels}>
                {labels[1]}
              </CloudLabel>
              <CloudLabel position="right" showLabels={showLabels}>
                {labels[2]}
              </CloudLabel>
            </SliderContainer>
          </ContentContainer>
        </StyledCard>
      </SnackbarContent>
    );
  }
);

LevelOfDetailsSlider.displayName = "LevelOfDetailsSlider";

export default LevelOfDetailsSlider;
